import { z } from 'zod';
import { SuggestionTypes } from '../statuses';
import { csvArray, listQuerySchema, optionalTrimmed, uuid } from './common';
import { DEFAULT_OBSERVATION_DAYS } from '../metrics';
const enumOf = (values) => z.enum(values);
/** 分析の共通クエリ（docs/05-api-spec.md 第11章） */
export const analyticsQuerySchema = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '開始日は YYYY-MM-DD で指定してください').optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '終了日は YYYY-MM-DD で指定してください').optional(),
    observationDays: z
        .union([z.number(), z.string()])
        .optional()
        .transform((v) => {
        if (v == null || v === '')
            return DEFAULT_OBSERVATION_DAYS;
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n) ? Math.min(Math.max(Math.trunc(n), 1), 180) : DEFAULT_OBSERVATION_DAYS;
    }),
    productId: uuid.optional(),
    campaignId: uuid.optional(),
    assigneeId: z.string().max(40).optional(),
    channel: z.enum(['form', 'email']).optional(),
    /** 計測中の企業を分母から除く表示への切替（docs/15-glossary.md 3.3） */
    excludeMeasuring: z.union([z.boolean(), z.string()]).optional(),
});
export const timeseriesQuerySchema = analyticsQuerySchema.extend({
    granularity: z.enum(['day', 'week']).default('day'),
});
export const breakdownQuerySchema = analyticsQuerySchema.extend({
    dimension: z.enum(['user', 'product', 'campaign', 'channel']).default('campaign'),
});
export const suggestionSchema = z.object({
    id: uuid,
    type: enumOf(SuggestionTypes.values),
    targetType: z.string().nullable(),
    targetId: uuid.nullable(),
    targetLabel: z.string().nullable(),
    title: z.string(),
    body: z.string(),
    /** 推測には「仮説」ラベルを付す（docs/02-functional-spec.md F10.2） */
    isHypothesis: z.boolean(),
    confidence: z.number().nullable(),
    rationale: z.object({
        dataPeriod: z.object({ from: z.string(), to: z.string() }).nullable(),
        sampleSize: z.number().int().nullable(),
        references: z.array(z.record(z.string(), z.unknown())).default([]),
        note: z.string().nullable(),
    }),
    decision: z.enum(['pending', 'adopted', 'rejected']),
    decidedBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    decidedAt: z.string().nullable(),
    createdAt: z.string(),
});
export const suggestionsQuerySchema = listQuerySchema.extend({
    type: csvArray(enumOf(SuggestionTypes.values)),
    decision: csvArray(enumOf(['pending', 'adopted', 'rejected'])),
});
export const rejectSuggestionSchema = z.object({
    reason: optionalTrimmed(500),
});
// ---------------------------------------------------------------- dashboard
export const dashboardSchema = z.object({
    period: z.object({
        from: z.string(),
        to: z.string(),
        observationDays: z.number().int(),
        measuringCount: z.number().int(),
    }),
    totals: z.object({
        companies: z.number().int(),
        sent: z.number().int(),
        replied: z.number().int(),
        appointments: z.number().int(),
        securityRisk: z.object({
            high: z.number().int(),
            medium: z.number().int(),
            low: z.number().int(),
            unknown: z.number().int(),
        }),
        newsletterConfirmed: z.number().int(),
    }),
    metrics: z.object({
        collected: z.number().int(),
        collectedDelta: z.number().int(),
        sentCompanies: z.number().int(),
        sentDelta: z.number().int(),
        repliedCompanies: z.number().int(),
        replyRate: z.number().nullable(),
        appointmentCompanies: z.number().int(),
        appointmentRate: z.number().nullable(),
    }),
    needsAttention: z.object({
        unknownSends: z.number().int(),
        needsActionSends: z.number().int(),
        openReplies: z.number().int(),
        overdueActivities: z.number().int(),
        unlinkedInbound: z.number().int(),
    }),
    running: z.array(z.object({
        kind: z.enum(['collection', 'campaign', 'job']),
        id: uuid,
        label: z.string(),
        done: z.number().int(),
        total: z.number().int(),
        status: z.string(),
        href: z.string(),
        canCancel: z.boolean(),
    })),
    todayAppointments: z.array(z.object({
        id: uuid,
        companyId: uuid,
        companyName: z.string(),
        scheduledAt: z.string(),
        format: z.string(),
        location: z.string().nullable(),
    })),
    suggestions: z.array(z.object({ id: uuid, type: z.string(), title: z.string(), isHypothesis: z.boolean() })),
    quota: z.object({
        aiCost: z.object({ limitUsd: z.number().nullable(), usedUsd: z.number(), level: z.string() }),
        search: z.object({ limitCalls: z.number().nullable(), usedCalls: z.number(), level: z.string() }),
        send: z.object({ dailyLimit: z.number().nullable(), usedToday: z.number(), level: z.string() }),
    }),
});
//# sourceMappingURL=analytics.js.map